import { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store/hooks';
import { setSelectedApplication } from '../../store/slices/application.slice';
import { useApplication } from '../../hooks/useApplication';

export default function ApplicationSelector() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { selectedAppId, availableApps, loading } = useApplication();
  const [showLoading, setShowLoading] = useState(true);

  const currentApp = availableApps.find((app) => app.id === selectedAppId);
  const displayName = currentApp?.name || t('labels.allApplications');

  const handleSelect = (appId: string | null) => {
    console.log('[ApplicationSelector] handleSelect called with appId:', appId);
    dispatch(setSelectedApplication(appId));
    console.log('[ApplicationSelector] dispatch completed');
  };

  // Stop showing loading after 2 seconds
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(true);
    }
  }, [loading]);

  // Always show the selector, even while loading
  const isLoading = loading && showLoading;

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors max-w-[140px] sm:max-w-xs">
        {isLoading && (
          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-indigo-600 flex-shrink-0"></div>
        )}
        <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[80px] sm:max-w-[200px]" title={displayName}>
          {displayName}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-64 origin-top-left sm:origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-96 overflow-y-auto z-50">
          <div className="p-1">
            {/* All Applications Option */}
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => handleSelect(null)}
                  className={`${
                    active ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  } ${
                    selectedAppId === null ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''
                  } group flex w-full items-center rounded-md px-3 py-2 text-sm`}
                >
                  <span className="flex-1 text-left text-gray-900 dark:text-gray-100">
                    {t('labels.allApplications')}
                  </span>
                  {selectedAppId === null && (
                    <CheckIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  )}
                </button>
              )}
            </Menu.Item>

            {availableApps.length > 0 && (
              <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
            )}

            {/* Individual Applications */}
            {availableApps.map((app) => (
              <Menu.Item key={app.id}>
                {({ active }) => (
                  <button
                    onClick={() => handleSelect(app.id)}
                    className={`${
                      active ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                    } ${
                      selectedAppId === app.id ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''
                    } group flex w-full items-center rounded-md px-3 py-2 text-sm`}
                    title={app.name}
                  >
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-gray-900 dark:text-gray-100 truncate">{app.name}</div>
                      {app.environment && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {app.environment}
                        </div>
                      )}
                    </div>
                    {selectedAppId === app.id && (
                      <CheckIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0 ml-2" />
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}

            {availableApps.length === 0 && !isLoading && (
              <div className="px-3 pt-4 pb-1 text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('labels.noApplications')}
                </p>
              </div>
            )}
            {availableApps.length === 0 && isLoading && (
              <div className="px-3 py-6 text-center">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('labels.loading')}
                </p>
              </div>
            )}

            {/* Ação sempre presente: criar nova aplicação */}
            {!isLoading && (
              <>
                <div className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => navigate('/settings/applications?new=1')}
                      className={`${active ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''} group flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400`}
                    >
                      <PlusIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 text-left">{t('labels.createApplication', 'Criar nova aplicação')}</span>
                    </button>
                  )}
                </Menu.Item>
              </>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
